import { Checkbox } from '@cian/ui-kit';

import * as React from 'react';

import { type NonEmptyArray } from '@/packages/JsonQuery';
import { EEntranceType } from '@/packages/api-models/common/json_query';

import { CheckboxButtonGroupUnselectable } from '../../CheckboxButtonGroupUnselectable';
import { Filter } from '../common/Filter';
import { InlineFilter } from '../common/InlineFilter';
import { InlineFilterGroup } from '../common/InlineFilterGroup';

import { ENTRANCE_TYPE_OPTIONS, SEPARATE_ENTRANCE_TYPES } from './constants';
import { EInternalEntranceType } from './types';

/**
 * С улицы | Со двора | Отдельных вход | Результат
 *    +    |    +     |       +        |  [1, 2]
 *    +    |    -     |       +        |  [2]
 *    -    |    +     |       +        |  [1]
 *    -    |    -     |       +        |  [1, 2] (Выбираю сразу все опции для консистентности UI и состояния)
 *    +    |    +     |       -        |  [1, 2, 3, 4]
 *    +    |    -     |       -        |  [2, 4]
 *    -    |    +     |       -        |  [1, 3]
 *    -    |    -     |       -        |  null
 */

interface IEntranceProps {
  value: NonEmptyArray<EEntranceType> | null;
  onChange(value: NonEmptyArray<EEntranceType> | null): void;
}

export const Entrance: React.FC<IEntranceProps> = ({ value, onChange }) => {
  // Нет смысла заворачивать в useMemo или обновлять через useEffect,
  // поскольку value каждый раз приходит с новой ссылкой на массив.
  // Мемоизация будет иметь смысл если это пофиксить.
  const isSeparateEntrance = getCheckboxState(value);
  const entranceTypes = getInternalEntranceTypes(value);

  const updateEntranceTypes = React.useCallback(
    (internalEntranceType: NonEmptyArray<EInternalEntranceType> | null, isSeparate: boolean) => {
      if (!internalEntranceType) {
        onChange(null);

        return;
      }

      const result = internalEntranceType.reduce((acc, item) => {
        switch (item) {
          case 'courtyard':
            acc.push(EEntranceType.SeparateCourtyard);

            if (!isSeparate) {
              acc.push(EEntranceType.CommonCourtyard);
            }
            break;
          case 'street':
            acc.push(EEntranceType.SeparateStreet);

            if (!isSeparate) {
              acc.push(EEntranceType.CommonStreet);
            }
            break;
        }

        return acc;
      }, [] as EEntranceType[]);

      onChange(result.length > 0 ? (result as NonEmptyArray<EEntranceType>) : null);
    },
    [onChange],
  );

  const handleEntranceTypesChange = React.useCallback(
    (internalEntranceTypes: NonEmptyArray<EInternalEntranceType> | null) => {
      updateEntranceTypes(internalEntranceTypes, isSeparateEntrance);
    },
    [isSeparateEntrance, updateEntranceTypes],
  );

  const handleIsSeparateEntranceChange = (_: React.ChangeEvent, isSeparate: boolean): void => {
    // Если просто нажали на галочку "Отдельный вход", то нужно добавить все типы входов
    if (!value && isSeparate) {
      updateEntranceTypes(Object.values(EInternalEntranceType) as NonEmptyArray<EInternalEntranceType>, isSeparate);
    } else {
      updateEntranceTypes(entranceTypes, isSeparate);
    }
  };

  return (
    <Filter id="advancedFilter_entrance" label="Вход">
      <InlineFilterGroup>
        <InlineFilter>
          <CheckboxButtonGroupUnselectable
            options={ENTRANCE_TYPE_OPTIONS}
            value={entranceTypes}
            onChange={handleEntranceTypesChange}
          />
        </InlineFilter>
        <InlineFilter>
          <Checkbox checked={isSeparateEntrance} label="Отдельный вход" onChange={handleIsSeparateEntranceChange} />
        </InlineFilter>
      </InlineFilterGroup>
    </Filter>
  );
};

function getCheckboxState(value: NonEmptyArray<EEntranceType> | null): boolean {
  if (!value) {
    return false;
  }

  // Возвращает true, если содержит только separate параметры
  return value.every((item: EEntranceType) => SEPARATE_ENTRANCE_TYPES.includes(item));
}

function getInternalEntranceTypes(
  value: NonEmptyArray<EEntranceType> | null,
): NonEmptyArray<EInternalEntranceType> | null {
  if (!value) {
    return null;
  }

  const result = value.reduce((acc, item) => {
    switch (item) {
      case EEntranceType.SeparateCourtyard:
      case EEntranceType.CommonCourtyard:
        acc.add(EInternalEntranceType.Courtyard);
        break;
      case EEntranceType.SeparateStreet:
      case EEntranceType.CommonStreet:
        acc.add(EInternalEntranceType.Street);
        break;
    }

    return acc;
  }, new Set<EInternalEntranceType>());

  return result.size > 0 ? ([...result] as NonEmptyArray<EInternalEntranceType>) : null;
}
